import { useMemo } from 'react';

import { getDisabledReason } from '@frontend/constants';
import { useOperations } from '@frontend/hooks';

/**
 * Hook to get workflow-related permissions.
 * Returns permission flags for workflow trigger and agent management.
 *
 * This is a base hook used by:
 * - useWorkflowNodePermissions (for node-specific permission calculations)
 * - useWorkflowAgent (for setting deletable flag on graph nodes)
 * - ActionsCell (for agent list action buttons)
 * - WorkflowAgentPage (for form read-only state)
 */
export function useWorkflowPermissions() {
  const { canUser, canInstance } = useOperations();

  return useMemo(() => {
    // User-level permissions
    const canManageWorkflowAgents = canUser(['manage_workflow_agents']);
    const canManageWorkflowTriggers = canUser(['manage_workflow_triggers']);

    // Instance-level capabilities
    const hasWorkflowCapability = canInstance(['workflows_full_enabled', 'workflows_chat_enabled']);
    const hasAgentCreationCapability = canInstance(['agent_creation_enabled']);

    // Composite/calculated permissions for workflow agents
    // Create/Edit: Requires workflow capability + user permission
    const canCreateWorkflowAgents =
      hasWorkflowCapability && hasAgentCreationCapability && canManageWorkflowAgents;
    const canEditWorkflowAgents = canCreateWorkflowAgents;

    // Delete: Only requires user permission (allowed even when capability is OFF for cleanup)
    const canDeleteWorkflowAgents = canManageWorkflowAgents;

    // Install from directory: Only requires user permission (same as delete)
    const canInstallWorkflowAgents = canManageWorkflowAgents;

    // View: Requires view_agents permission (users can see their agents after tier downgrade if they have this permission)
    const canViewWorkflowAgents = canUser(['view_agents']);

    return {
      canManageWorkflowAgents,
      canManageWorkflowTriggers,
      hasWorkflowCapability,
      hasAgentCreationCapability,
      canCreateWorkflowAgents,
      canEditWorkflowAgents,
      canDeleteWorkflowAgents,
      canInstallWorkflowAgents,
      canViewWorkflowAgents,
      createAgentDisabledReason: getDisabledReason(
        hasWorkflowCapability && hasAgentCreationCapability,
        canManageWorkflowAgents
      ),
      editAgentDisabledReason: getDisabledReason(
        hasWorkflowCapability && hasAgentCreationCapability,
        canManageWorkflowAgents
      ),
      deleteAgentDisabledReason: getDisabledReason(true, canManageWorkflowAgents),
    };
  }, [canUser, canInstance]);
}
